import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Upload, File as FileIcon, Trash2, Eye, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/business/PageHeader";
import EmptyState from "@/components/business/EmptyState";
import DocumentViewer from "@/components/business/DocumentViewer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inr } from "@/lib/format";

const DOC_TYPES = [
  { value: "form16", label: "Form 16" },
  { value: "salary_slip", label: "Salary Slip" },
  { value: "pan", label: "PAN" },
  { value: "aadhaar", label: "Aadhaar" },
  { value: "bank_statement", label: "Bank Statement" },
  { value: "broker_statement", label: "Broker / Mutual Fund Statement" },
  { value: "rental_receipt", label: "Rental Receipt" },
  { value: "interest_certificate", label: "Interest Certificate" },
  { value: "insurance_proof", label: "Insurance / Investment Proof" },
];

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [doc_type, setDocType] = useState("form16");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInput = useRef(null);

  const load = () => api.get("/documents").then(({ data }) => setDocs(data));
  useEffect(() => { load(); }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", doc_type);
    try {
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded · ${data.extracted ? Object.keys(data.extracted).length + " fields extracted" : "ready"}`);
      load();
      setPreview(data);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/documents/${id}`);
    toast.success("Document deleted");
    load();
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <PageHeader>
          <PageHeader.Title>Documents</PageHeader.Title>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground">Upload tax documents. The AI extracts key fields and auto-fills your return.</p>

        <Card className="mt-6 p-6 border-border" data-testid="upload-card">
          <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end">
            <div>
              <Label className="text-xs">Document type</Label>
              <Select value={doc_type} onValueChange={setDocType}>
                <SelectTrigger className="h-11 mt-1.5" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => fileInput.current?.click()} disabled={uploading} className="h-11" data-testid="upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
            <input ref={fileInput} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={onPick} data-testid="file-input" />
          </div>
          <div className="mt-4 text-xs text-muted-foreground">Supported: PDF, JPG, PNG. AES-256 encrypted at rest.</div>
        </Card>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="documents-grid">
          {docs.length === 0 && (
            <EmptyState
              icon={FileIcon}
              title="No documents yet."
              description="Upload your Form 16 to begin — AI extracts key fields and auto-fills your return."
              className="col-span-full"
            />
          )}
          {docs.map((d) => (
            <Card key={d.id} className="p-5 border-border hover:border-primary/40 transition-colors">
              <div className="flex items-start gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-md bg-primary/8 text-primary"><FileIcon className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{d.file_name}</div>
                  <div className="text-xs text-muted-foreground capitalize">{d.doc_type.replace(/_/g, " ")} · {(d.file_size / 1024).toFixed(1)} KB</div>
                </div>
              </div>
              {d.status === "extracted" && d.extracted && (
                <div className="mt-3 flex items-center gap-1 text-xs text-accent">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {Object.keys(d.extracted).filter(k => !k.startsWith("_")).length} fields extracted
                </div>
              )}
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPreview(d)} data-testid={`preview-${d.id}`}><Eye className="h-3.5 w-3.5 mr-1" /> View</Button>
                <Button variant="outline" size="sm" onClick={() => remove(d.id)}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
              </div>
            </Card>
          ))}
        </div>

        <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{preview?.file_name}</DialogTitle>
              <DialogDescription className="capitalize">{preview?.doc_type?.replace(/_/g, " ")}</DialogDescription>
            </DialogHeader>
            <DocumentViewer document={preview} />
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
